/**
 * navDropdown — Alpine.js component for an accessible disclosure-pattern nav dropdown.
 *
 * Implements the ARIA disclosure navigation pattern:
 *   - No role="menu" — the submenu is a plain <ul> inside a landmark <nav>
 *   - aria-expanded on the chevron <button> reflects open/closed state reactively
 *   - Submenu opens on mouseenter and closes on mouseleave (pointer users)
 *   - Chevron <button> still opens/closes via click and keyboard (non-pointer users)
 *   - Escape closes the submenu and returns focus to the toggle button
 *   - Arrow keys (↑ / ↓) move focus through the submenu's direct focusable children
 *   - Clicking outside closes the submenu
 *   - Focus leaving the component closes the submenu
 *
 * Usage in HTML:
 *   <li data-x-data="navDropdown">
 *     <a href="/page">Page</a>
 *     <button data-nav-toggle ...>chevron</button>
 *     <ul data-nav-submenu ...>
 *       <li><a href="...">Child</a></li>
 *     </ul>
 *   </li>
 *
 * The component is intentionally style-free — all visual treatment is
 * applied via Tailwind classes directly in the Razor view.
 */
export default function navDropdown() {
    return {
        open: false,
        _toggle: null,

        init() {
            this._toggle = this.$el.querySelector('[data-nav-toggle]');
        },

        // ── Open / close ─────────────────────────────────────────────────

        show() {
            this.open = true;
        },

        hide() {
            this.open = false;
        },

        toggle() {
            this.open ? this.hide() : this.show();
        },

        /**
         * Close and return focus to the toggle button.
         * Called by Escape keydown handlers in the view.
         */
        closeAndRefocus() {
            this.hide();
            this.$nextTick(() => this._toggle?.focus());
        },

        // ── Keyboard navigation inside the open submenu ───────────────────

        /**
         * Handle ↑ / ↓ arrow keys within the submenu.
         * Moves focus between the direct focusable children of [data-nav-submenu].
         *
         * @param {'up'|'down'} direction
         */
        arrowNav(direction) {
            const submenu = this.$el.querySelector('[data-nav-submenu]');
            if (!submenu) return;

            const items = Array.from(
                submenu.querySelectorAll(':scope > li > a, :scope > li > [data-nav-toggle]')
            ).filter(el => !el.closest('[data-x-data="navDropdown"]')?.dataset || el.closest('[data-nav-submenu]') === submenu);

            if (items.length === 0) return;

            const current = document.activeElement;
            const idx = items.indexOf(current);

            if (direction === 'down') {
                const next = idx < items.length - 1 ? items[idx + 1] : items[0];
                next.focus();
            } else {
                const prev = idx > 0 ? items[idx - 1] : items[items.length - 1];
                prev.focus();
            }
        },

        // ── Focusout guard ────────────────────────────────────────────────

        /**
         * Close the submenu when focus leaves the entire <li> component.
         * relatedTarget is the element receiving focus; if it is outside
         * this component's root element the submenu should close.
         *
         * @param {FocusEvent} event
         */
        handleFocusOut(event) {
            if (!this.$el.contains(event.relatedTarget)) {
                this.hide();
            }
        },
    };
}
